import {useState} from 'react'
import {Link, useParams, useSearchParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {InfoTip} from '@/components/ui/info-tip'
import {HelpIcon, GroupHeader} from '@/components/help-icon'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {FiltrosDeEstado, type OpcionEstado} from '@/components/ui/filtros-de-estado'
import {DataTable, type Column, type ColumnGroup} from '@/components/data-table'
import {PageHeading} from '@/components/page-heading'
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs'
import {useOperatorPage} from '@/components/no-access-state'
import {CambioOcupacionSection} from '@/components/cambio-ocupacion-section'
import {CertificacionTab} from '@/components/certificacion-tab'
import {useTableState} from '@/hooks/common/use-table-state'
import {useResumenCambios} from '@/hooks/salud/medicina_laboral/use-cambio-ocupacion'
import {
  useExamenesHuecos,
  useExamenesMatriz,
  type ExamenCelda,
  type ExamenColumna,
  type ExamenFila,
  type MatrizEstadoFiltro,
} from '@/hooks/sgsst/evidencias/use-examenes-matriz'
import {soporteEstadoTone} from '@/lib/sgsst-readiness'
import {formatDateOnly, formatShortDate} from '@/lib/risk'
import {SearchInput} from '@/components/ui/search-input'

type EstadoFiltro = MatrizEstadoFiltro | 'todos'

function FiltrosEstadoMatriz({
  estado,
  onEstado,
  totalActivos,
  totalAlertas,
  conteoSinExamen,
  conteoVencido,
}: {
  estado: EstadoFiltro
  onEstado: (estado: EstadoFiltro) => void
  totalActivos: number
  totalAlertas: number | null
  conteoSinExamen: number
  conteoVencido: number
}) {
  const {t} = useTranslation()
  const opciones: OpcionEstado<EstadoFiltro>[] = [
    {valor: 'todos', etiqueta: t('examenesMatriz.filtro.todos'), conteo: totalActivos},
    // «Por revisar» es dato de salud: sin custodia `total_alertas` llega null y el componente esconde la opción.
    {valor: 'por_revisar', etiqueta: t('examenesMatriz.filtro.porRevisar'), conteo: totalAlertas},
    {valor: 'sin_examen', etiqueta: t('examenesMatriz.filtro.sinExamen'), conteo: conteoSinExamen},
    {valor: 'vencido', etiqueta: t('examenesMatriz.filtro.vencido'), conteo: conteoVencido},
  ]
  return (
    <FiltrosDeEstado opciones={opciones} valor={estado} onValor={onEstado} testIdPrefijo="examenes-matriz-filtro" />
  )
}

// El backend manda el título largo de la casilla; el sufijo de la key ya distingue el tipo, así que un
// mapa corto basta sin tocar el motor.
const TIPO_POR_SUFIJO: Record<string, string> = {
  examen_ingreso: 'ingreso',
  examen_periodico: 'periodico',
  examen_egreso: 'egreso',
  examen_post_incapacidad: 'post_incapacidad',
  examen_cambio_ocupacion: 'cambio_ocupacion',
  examen_reintegro: 'reintegro',
}

function tipoDeColumna(key: string): string | null {
  const sufijo = key.split('/')[1]
  return (sufijo && TIPO_POR_SUFIJO[sufijo]) ?? null
}

type TabKey = 'matriz' | 'sugerencias' | 'certificacion'

const TABS: TabKey[] = ['matriz', 'sugerencias', 'certificacion']

function parseTab(value: string | null): TabKey | null {
  return TABS.find((clave) => clave === value) ?? null
}

const PAGE_SIZE = 20

function CeldaExamen({celda}: {celda: ExamenCelda}) {
  const {t} = useTranslation()
  if (celda.estado === 'no_aplica') {
    return (
      <InfoTip
        label={t('examenesMatriz.celdaNoAplicaLabel')}
        tip={t('examenesMatriz.celdaNoAplicaTip')}
        className="text-xs">
        —
      </InfoTip>
    )
  }
  if (celda.estado === 'falta') {
    // «Falta» es un estado definido, no una ausencia: chip neutro; ámbar y rojo son para vencimientos.
    return (
      <Tag tone="neutral" data-testid="examen-celda-falta">
        {t('examenesMatriz.celdaFalta')}
      </Tag>
    )
  }
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      <Tag tone={soporteEstadoTone(celda.estado)} data-testid="examen-celda-estado">
        {t(`evidence.soportes.estado.${celda.estado}`)}
      </Tag>
      {celda.vence_el && (
        <span className="font-mono text-[11px] whitespace-nowrap text-muted-foreground">
          {t(celda.estado === 'vencido' ? 'examenesMatriz.celdaVencioEl' : 'examenesMatriz.celdaVenceEl', {
            date: formatShortDate(celda.vence_el),
          })}
        </span>
      )}
    </span>
  )
}

/** Columna propia (E1-H09, §9): una valoración es un examen aparte, y meterla entre las casillas de la
 * Res. 0312 diría que el examen ocupacional está hecho. Nunca rojo por una periodicidad no verificada. */
function CeldaValoraciones({businessId, fila}: {businessId: string | undefined; fila: ExamenFila}) {
  const {t} = useTranslation()
  // `null` no es lista vacía: es «no le corresponde ver este dato», no «no tiene ninguna» (S-2).
  if (fila.valoraciones === null || fila.valoraciones === undefined) {
    return <span className="text-xs text-muted-foreground">{t('valoracion.matriz.sinAcceso')}</span>
  }
  if (fila.valoraciones.length === 0) {
    return <span className="text-xs text-muted-foreground">{t('valoracion.matriz.vacio')}</span>
  }
  return (
    <span className="flex flex-col items-start gap-1" data-testid="examen-celda-valoraciones">
      {fila.valoraciones.map((celda) => (
        <span key={celda.clase} className="flex flex-col items-start gap-0.5">
          <Link
            to={`/businesses/${businessId}/empleados/${fila.employee_id}?tab=examenes`}
            aria-label={t('valoracion.matriz.verFicha', {nombre: `${fila.name} ${fila.last_name}`})}>
            <Tag
              tone={soporteEstadoTone(celda.estado)}
              data-testid={`valoracion-celda-${celda.clase}`}
              title={[
                celda.veredicto,
                celda.no_habilita ? t('valoracion.noHabilita') : null,
                celda.sin_periodicidad ? t('valoracion.sinPeriodicidad') : null,
                celda.sin_norma_cargada ? t('valoracion.sinNormaCargada') : null,
                celda.aportada_no_exigida ? t('valoracion.aportada') : null,
                celda.no_exigible ? t('valoracion.yaNoExigibleSinFecha') : null,
              ]
                .filter(Boolean)
                .join(' · ')}>
              {t(`valoracion.clase.${celda.clase}`)}
            </Tag>
          </Link>
          {celda.vence_el && (
            <span className="font-mono text-[11px] text-muted-foreground">
              {t(celda.estado === 'vencido' && !celda.no_habilita ? 'valoracion.vencioEl' : 'valoracion.venceEl', {
                fecha: formatDateOnly(celda.vence_el),
              })}
            </span>
          )}
        </span>
      ))}
    </span>
  )
}

export function ExamenesMatrizPage() {
  const {t} = useTranslation()
  const {businessId} = useParams()
  const {guard} = useOperatorPage(businessId)
  const table = useTableState('name')
  const [estadoFiltro, setEstadoFiltro] = useState<EstadoFiltro>('todos')
  const {data, isLoading} = useExamenesMatriz(businessId, {
    page: table.page,
    search: table.search,
    estado: estadoFiltro === 'todos' ? undefined : estadoFiltro,
  })
  const {data: huecos} = useExamenesHuecos(businessId)
  // Mismo `queryKey` que la tab: react-query lo sirve de una sola petición. El conteo sale del resumen y
  // no de la bandeja, para no depender de la página.
  const {data: sugerencias} = useResumenCambios(businessId)
  const [searchParams, setSearchParams] = useSearchParams()

  if (guard) return guard

  const tab = parseTab(searchParams.get('tab')) ?? 'matriz'
  function setTab(next: TabKey) {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        params.set('tab', next)
        return params
      },
      {replace: true}
    )
  }

  function onEstadoFiltro(next: EstadoFiltro) {
    setEstadoFiltro(next)
    table.setPage(1)
  }

  const columnas = data?.columnas ?? []
  const columns: Column<ExamenFila>[] = [
    {
      key: 'name',
      header: t('examenesMatriz.columnEmpleado'),
      width: 'w-48',
      cell: (fila) => (
        <span className="flex flex-col items-start gap-1">
          <span className="block truncate font-medium text-foreground">
            {fila.name} {fila.last_name}
          </span>
          {/* E1-H06: la casilla puede estar vigente y la persona seguir deshabilitada: son dos preguntas. */}
          {fila.concepto_alerta && (
            <Link
              to={`/businesses/${businessId}/empleados/${fila.employee_id}?tab=examenes`}
              aria-label={t('examenesMatriz.conceptoAlertaLink', {name: `${fila.name} ${fila.last_name}`})}
              data-testid={`examen-concepto-alerta-${fila.employee_id}`}>
              <Tag tone="red" title={t(`examenes.normaAnterior.rejilla.${fila.concepto_alerta}`)}>
                {t('examenesMatriz.conceptoAlertaCorta')}
              </Tag>
            </Link>
          )}
        </span>
      ),
    },
    ...columnas.map((columna: ExamenColumna): Column<ExamenFila> => {
      const tipo = tipoDeColumna(columna.key)
      return {
        key: columna.key,
        // `columna.titulo` es el respaldo si llega una casilla médica que el mapa no conoce.
        header: tipo ? t(`examenesMatriz.columnaConcepto.${tipo}`) : columna.titulo,
        width: 'w-28',
        cell: (fila) => {
          const celda = fila.celdas.find((c) => c.key === columna.key)
          return celda ? <CeldaExamen celda={celda} /> : <span className="text-xs text-muted-foreground">—</span>
        },
      }
    }),
    {
      key: 'valoraciones',
      header: t('valoracion.matriz.columna'),
      headerAside: (
        <HelpIcon
          label={t('common.help.iconLabelFor', {campo: t('valoracion.matriz.columna')})}
          testId="examenes-matriz-leyenda-valoraciones">
          {t('valoracion.matriz.leyenda')}
        </HelpIcon>
      ),
      cell: (fila) => <CeldaValoraciones businessId={businessId} fila={fila} />,
    },
  ]

  // Vacío si no hay columnas de tipo reconocidas: sin grupos la tabla pinta su único `<thead>`.
  const columnGroups: ColumnGroup[] =
    columnas.length > 0 && columnas.every((c) => tipoDeColumna(c.key))
      ? [
          {
            startKey: columnas[0].key,
            span: columnas.length,
            content: (
              <GroupHeader
                label={t('examenesMatriz.columnaConceptoLabel')}
                help={t('examenesMatriz.columnaConceptoAyuda')}
                testId="examenes-matriz-columna-concepto"
              />
            ),
          },
        ]
      : []

  return (
    <div className="space-y-5" data-testid="examenes-matriz-page">
      <PageHeading title={t('examenesMatriz.title')} />
      {/* E1-H11: las sugerencias no pintan rojo ni deshabilitan a nadie; la casilla se enciende al confirmar. */}
      <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)}>
        <TabsList>
          <TabsTrigger value="matriz" data-testid="tab-matriz">
            {t('examenesMatriz.tabs.matriz')}
          </TabsTrigger>
          <TabsTrigger value="sugerencias" data-testid="tab-sugerencias">
            <span className="inline-flex items-center gap-1.5">
              {t('examenesMatriz.tabs.sugerencias')}
              {!!sugerencias?.total && (
                <Tag tone="amber" data-testid="tab-sugerencias-chip">
                  {sugerencias.total}
                </Tag>
              )}
            </span>
          </TabsTrigger>
          <TabsTrigger value="certificacion" data-testid="tab-certificacion">
            {t('examenesMatriz.tabs.certificacion')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="matriz" className="pt-4 space-y-4">
          <div className="surface overflow-hidden">
            <div className="surface-divider flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
              <SearchInput
                value={table.searchInput}
                onChange={table.setSearchInput}
                placeholder={t('examenesMatriz.searchPlaceholder')}
                data-testid="examenes-matriz-search"
              />
              {data && (
                <FiltrosEstadoMatriz
                  estado={estadoFiltro}
                  onEstado={onEstadoFiltro}
                  totalActivos={data.total_activos}
                  totalAlertas={data.total_alertas}
                  conteoSinExamen={data.conteo_sin_examen}
                  conteoVencido={data.conteo_vencido}
                />
              )}
            </div>

            {isLoading && !data ? (
              <div className="p-4">
                <Skeleton className="h-64 w-full" />
              </div>
            ) : data && data.items.length === 0 ? (
              <p className="px-4 py-10 text-center text-sm text-muted-foreground" data-testid="examenes-matriz-empty">
                {t('examenesMatriz.empty')}
              </p>
            ) : (
              <>
                <div className="px-1">
                  <DataTable
                    columns={columns}
                    rows={data?.items ?? []}
                    rowKey={(fila) => fila.employee_id}
                    columnGroups={columnGroups}
                  />
                </div>
                {!!data?.total && (
                  <div className="surface-divider border-t px-4 py-2.5">
                    <PaginationBar
                      page={table.page}
                      pageSize={PAGE_SIZE}
                      total={data.total}
                      onPageChange={table.setPage}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </TabsContent>

        <TabsContent value="sugerencias" className="pt-4">
          <CambioOcupacionSection businessId={businessId!} />
        </TabsContent>

        <TabsContent value="certificacion" className="pt-4">
          <CertificacionTab businessId={businessId!} artefacto="examenes" huecosExamenes={huecos} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
